/**
 * Availability Dialog Component
 *
 * Modal dialog for adding one-off availability blocks for mechanics.
 * This is for extra hours outside of regular scheduled availability.
 *
 * @module Calendar/dialogs/AvailabilityDialog
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';

const envVariable = require('../../../EnvironmentVariables.json');

/**
 * Availability Dialog
 *
 * Allows creation of one-off availability blocks for mechanics (e.g., extra hours).
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {Array<Object>} props.mechanics - List of available mechanics
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onSuccess - Callback when availability is successfully created
 * @returns {React.Element} Availability dialog
 */
export const AvailabilityDialog = ({ open, mechanics, selectedLocationId, onClose, onSuccess }) => {
  // Get today's date in YYYY-MM-DD format for date input default value
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [selectedMechanic, setSelectedMechanic] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [timeErr, setTimeErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeErr(false);
    setFeedback(null);

    // Validate required fields
    if (!selectedDate) {
      setFeedback({ message: 'Please select a date', success: false });
      setLoading(false);
      return;
    }

    if (!selectedMechanic || selectedMechanic === '' || selectedMechanic === null || selectedMechanic === undefined) {
      setFeedback({ message: 'Please select a mechanic', success: false });
      setLoading(false);
      return;
    }

    if (!startTime || !endTime) {
      setFeedback({ message: 'Please enter both start and end times', success: false });
      setLoading(false);
      return;
    }

    const startTimeHour = parseInt(startTime.substring(0, 2));
    const startTimeMinute = parseInt(startTime.substring(3, 5));
    const endTimeHour = parseInt(endTime.substring(0, 2));
    const endTimeMinute = parseInt(endTime.substring(3, 5));

    // Validate time format
    if (isNaN(startTimeHour) || isNaN(startTimeMinute) || isNaN(endTimeHour) || isNaN(endTimeMinute)) {
      setTimeErr(true);
      setFeedback({ message: 'Invalid time format. Please use HH:MM format (e.g., 09:00)', success: false });
      setLoading(false);
      return;
    }

    if (
      startTime[2] !== ':' ||
      endTime[2] !== ':' ||
      startTime.length !== 5 ||
      endTime.length !== 5 ||
      startTimeHour > 23 ||
      startTimeMinute > 59 ||
      endTimeHour > 23 ||
      endTimeMinute > 59
    ) {
      setTimeErr(true);
      setFeedback({ message: 'Invalid time. Hours must be 0-23 and minutes must be 0-59', success: false });
      setLoading(false);
      return;
    }

    // Validate that end time is after start time (same day only)
    if (endTimeHour < startTimeHour || (endTimeHour === startTimeHour && endTimeMinute <= startTimeMinute)) {
      setTimeErr(true);
      setFeedback({ message: 'End time must be after start time. Availability must be within a single day.', success: false });
      setLoading(false);
      return;
    }

    // Build datetime strings in UTC format for backend
    let start_date_time_utc, end_date_time_utc;

    try {
      // Convert local times to UTC for backend (all backend endpoints expect UTC)
      const localStartDateTime = new Date(`${selectedDate}T${startTime}:00`);
      const localEndDateTime = new Date(`${selectedDate}T${endTime}:00`);

      // Validate that dates were parsed correctly
      if (isNaN(localStartDateTime.getTime()) || isNaN(localEndDateTime.getTime())) {
        throw new Error('Invalid date/time combination');
      }

      // Format as MySQL datetime in UTC
      start_date_time_utc = localStartDateTime.toISOString().slice(0, 19).replace('T', ' ');
      end_date_time_utc = localEndDateTime.toISOString().slice(0, 19).replace('T', ' ');
    } catch (error) {
      console.error('[AvailabilityDialog] Date parsing error:', error);
      setFeedback({ message: 'Invalid date or time. Please check your inputs and try again.', success: false });
      setLoading(false);
      return;
    }

    const SC_Id = localStorage.getItem('SC_Id');

    if (!SC_Id) {
      setFeedback({ message: 'Service center ID not found. Please log in again.', success: false });
      setLoading(false);
      return;
    }

    if (!selectedLocationId) {
      setFeedback({ message: 'Please choose a shop location first.', success: false });
      setLoading(false);
      return;
    }

    // Check for conflicting blocks before creating (use UTC times to match database)
    try {
      const conflictResponse = await fetch(envVariable.serverId + '/SCO/checkBlockConflicts.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mechanic_id: selectedMechanic,
          sc_id: SC_Id,
          location_id: selectedLocationId,
          start_date_time: start_date_time_utc,
          end_date_time: end_date_time_utc,
          block_type: 'availability',
          exclude_current: false,
        }),
      });

      const conflictResult = await conflictResponse.json();

      // Check for conflicts with availability blocks
      if (conflictResult.availability_conflicts && conflictResult.availability_conflicts.length > 0) {
        setFeedback({
          message: 'This availability period conflicts with an existing availability block for this mechanic',
          success: false
        });
        setLoading(false);
        return;
      }

      // Check for conflicts with leave blocks
      if (conflictResult.leave_conflicts && conflictResult.leave_conflicts.length > 0) {
        setFeedback({
          message: 'This availability period conflicts with a leave period for this mechanic',
          success: false
        });
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('[AvailabilityDialog] Error checking for conflicts:', error);
      // Continue anyway - don't block on conflict check failure
    }

    try {
      const response = await fetch(envVariable.serverId + '/mechanicSlots/saveFreeslot.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mech_id: selectedMechanic,
          sc_id: SC_Id,
          location_id: selectedLocationId,
          start_date_time: start_date_time_utc,
          end_date_time: end_date_time_utc,
        }),
      });

      const result = await response.json();

      if (result.type === 'success') {
        setFeedback({ message: 'Availability block created successfully!', success: true });
        // Call onSuccess after a short delay to show the success message
        setTimeout(async () => {
          if (onSuccess) {
            onSuccess(); // Triggers refresh in parent
            // Wait for refresh to complete before closing dialog
            // This ensures availability data is updated before user can interact
            await new Promise(resolve => setTimeout(resolve, 800));
          }
          handleClose();
        }, 800);
      } else {
        setFeedback({ message: result.message || 'Failed to create availability block', success: false });
      }
    } catch (error) {
      console.error('Error creating availability block:', error);
      setFeedback({
        message: 'Unexpected error occurred. Please try again.',
        success: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setSelectedDate(getTodayDateString());
    setStartTime('09:00');
    setEndTime('17:00');
    setSelectedMechanic('');
    setFeedback(null);
    setTimeErr(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Availability Block</DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Add a one-off availability block for a mechanic (e.g., extra hours outside regular schedule).
          </Typography>

          {/* Date Picker */}
          <TextField
            fullWidth
            label="Select Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          {/* Start Time */}
          <TextField
            fullWidth
            label="Start Time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          {/* End Time */}
          <TextField
            fullWidth
            label="End Time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          {timeErr && (
            <Typography color="error" variant="caption">
              Invalid time. End time must be after start time on the same day.
            </Typography>
          )}

          {/* Mechanic Selector */}
          <FormControl fullWidth required>
            <InputLabel>Select Mechanic</InputLabel>
            <Select
              label="Select Mechanic"
              value={selectedMechanic}
              onChange={(e) => setSelectedMechanic(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {mechanics.map((mechanic) => (
                <MenuItem key={mechanic.user_id} value={mechanic.user_id}>
                  {mechanic.first_name} {mechanic.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Feedback Message */}
          {feedback && (
            <Alert severity={feedback.success ? 'success' : 'error'}>{feedback.message}</Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !selectedMechanic || !selectedDate || !startTime || !endTime}
        >
          {loading ? <CircularProgress size={24} /> : 'Add Availability'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
